/**
 * MayaProfileChat — registration step 2 reskinned as a Maya-led chat.
 *
 * Shown during /register flow BEFORE the user has an account. Maya greets the
 * teen with her animated portrait (idle-loop video pulled from the public
 * `/api/ava-video/registration-greeter` endpoint — no auth required) and
 * asks the same 5 favourite questions that previously sat in a dropdown form.
 *
 * Answers are written back into Register.js's `formData` via the `onAnswer`
 * callback so the existing submit flow keeps working unchanged.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Send, ArrowRight, Loader2, Sparkles, Mic, MicOff, Volume2, VolumeX, Plus, X } from 'lucide-react';
import { IdleLoopVideo } from './chat/IdleLoopVideo';
import { SCRIPT, SCRIPT_DEMO } from './maya/script';
import { Bubble } from './maya/Bubble';
import { TEEN_VOICES } from '../pages/avaLibrary/data';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MayaProfileChat = ({ formData, onAnswer, onComplete, onBack, demoMode = false }) => {
  const { lang } = useLanguage();
  // undefined = fetch in flight (blocks seeding); object = resolved.
  const [scriptI18n, setScriptI18n] = useState(() =>
    (localStorage.getItem('hibff_lang') || 'en') === 'en' ? { steps: {} } : undefined
  );

  // Fetch Maya's translated script for non-English visitors (public endpoint).
  useEffect(() => {
    if (!lang || lang === 'en') { setScriptI18n({ steps: {} }); return; }
    setScriptI18n(undefined);
    fetch(`${API_URL}/api/tts/registration-script?lang=${lang}`)
      .then(r => r.json())
      .then(d => setScriptI18n(d && d.steps ? d : { steps: {} }))
      .catch(() => setScriptI18n({ steps: {} }));
  }, [lang]);

  // Pick the active script based on demoMode — 22-question full registration
  // OR the 4-question demo flow. Mutating SCRIPT directly would break the
  // module-level import; aliasing here keeps the runtime swap clean.
  const ACTIVE_SCRIPT = React.useMemo(() => {
    const base = demoMode ? SCRIPT_DEMO : SCRIPT;
    if (!scriptI18n || !scriptI18n.steps || !Object.keys(scriptI18n.steps).length) return base;
    const ov = scriptI18n.steps;
    const likert = scriptI18n.likert || [];
    return base.map((step) => {
      const key = demoMode && step.id === 'q_strength' ? 'demo_strength' : step.id;
      const o = ov[key] || {};
      const merged = { ...step };
      if (o.text) merged.text = o.text;
      if (o.ava_text) merged.ava_text = o.ava_text;
      if (o.placeholder) merged.placeholder = o.placeholder;
      if (step.type === 'choice' && Array.isArray(o.options) && o.options.length === step.options.length) {
        merged.options = step.options.map((val, i) => ({ value: val, label: o.options[i] || val }));
      }
      if (step.type === 'ocean' && likert.length === 5) {
        merged.options = step.options.map((opt, i) => ({ ...opt, label: likert[i] || opt.label }));
      }
      return merged;
    });
  }, [demoMode, scriptI18n]);
  const [stepIdx, setStepIdx] = useState(0);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const [chips, setChips] = useState([]); // multi-value inputs (e.g. hobbies)
  const [greeter, setGreeter] = useState({ name: 'Maya', avatar_url: null, idle_loop_url: null });
  // Gates the opening lines: Maya must not start speaking until her real
  // voice slot has resolved from the greeter endpoint. Otherwise the first
  // line plays at the default 'tf1' pitch (1.28×, rushed/robotic) and only
  // switches to her true voice on line two — the "voice changed back" bug.
  const [greeterLoaded, setGreeterLoaded] = useState(false);
  const [isListening, setIsListening] = useState(false);
  // Whether SpeechRecognition is supported in this browser. Used to gate the
  // mic-button render. Previously we read `recognitionRef.current` directly,
  // but mutating a ref does NOT trigger a re-render — so the mic icon was
  // invisible on first paint and only appeared after some other state change
  // forced a re-render. Tracking support as state guarantees deterministic
  // visibility from first render onwards.
  const [micSupported, setMicSupported] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true); // Maya speaks her questions aloud
  const [isSpeaking, setIsSpeaking] = useState(false);
  // Queue-driven bubble reveal — `pendingAva` holds Maya lines waiting to
  // be spoken; `activeAva` is the line currently being vocalised, with
  // `revealedChars` tracking how much of it is visible. Maya's voice is
  // the SAME OpenAI-TTS voice library used by the main chat (fetched via
  // the public /api/tts/registration endpoint) — NOT the robotic browser
  // speechSynthesis. Words reveal in sync with the mp3's real playback
  // position; if audio can't play, text still flows at a reading pace.
  const [pendingAva, setPendingAva] = useState([]); // Array<{ text, stepBubbleForId? }>
  const [activeAva, setActiveAva] = useState(null); // { text, stepBubbleForId?, revealedChars }
  const scrollerRef = useRef(null);
  const recognitionRef = useRef(null);
  const revealTimerRef = useRef(null); // interval handle for word-by-word fallback reveal
  const watchdogRef = useRef(null); // timeout guarding against audio that never starts
  const audioElRef = useRef(null); // the single <audio> element currently playing
  const audioCacheRef = useRef(new Map()); // `${voice}|${text}` → Promise<base64|null>
  const lineInFlightRef = useRef(null); // guards against double-starting the same line
  const lastAnsweredStepRef = useRef(null); // guards against double-tapped answers
  const spokenTextsRef = useRef(new Set()); // dedupe by content so the same line never speaks twice
  // Fresh mirrors of queue state. The answer controls live inside a
  // memoized block whose closures can capture STALE pendingAva/activeAva
  // (the memo doesn't rebuild when the queue drains). flushAvaToHistory
  // reads these refs instead, so a click can never re-flush a line that
  // already landed in history — the root cause of duplicated bubbles.
  const pendingAvaRef = useRef([]);
  const activeAvaRef = useRef(null);
  useEffect(() => { pendingAvaRef.current = pendingAva; }, [pendingAva]);
  useEffect(() => { activeAvaRef.current = activeAva; }, [activeAva]);
  // Teen's first name, captured the moment they answer Q1. Script lines
  // containing "{name}" are personalised at enqueue time so Maya uses
  // their name in both the bubble AND her voice.
  const nameRef = useRef('');
  const personalize = (t) => {
    if (!t) return t;
    const n = (nameRef.current || '').trim();
    if (!n) return t.replace(/,?\s*\{name\}/g, '').replace(/\s{2,}/g, ' ');
    return t.split('{name}').join(n);
  };

  // Pull Maya's avatar + idle loop video (public — no auth needed)
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/ava-video/registration-greeter`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setGreeter(d || {});
        // Kick off the avatar download immediately (browser starts fetching
        // the 1MB+ portrait now, in parallel with the 2s breather, instead
        // of waiting for React to mount the <img>). Cuts the visible delay.
        const url = (d && d.avatar_url) || null;
        if (url) { const img = new Image(); img.decoding = 'async'; img.src = url; }
        setGreeterLoaded(true);
      })
      .catch(() => { if (!cancelled) setGreeterLoaded(true); /* keep defaults, don't hang */ });
    return () => { cancelled = true; };
  }, []);

  // Browser-native speech recognition (no backend, no auth — same as ConversationsChat)
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return undefined;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = 'en-US';
    let finalTranscript = '';
    rec.onresult = (event) => {
      try {
        let interim = '';
        finalTranscript = '';
        const results = event && event.results ? event.results : [];
        for (let i = event.resultIndex || 0; i < results.length; i += 1) {
          const r = results[i];
          const t = r && r[0] && r[0].transcript ? r[0].transcript : '';
          if (r && r.isFinal) finalTranscript += t;
          else interim += t;
        }
        setDraft(finalTranscript + interim);
      } catch (_e) { /* never let a malformed speech event crash the wizard */ }
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
    setMicSupported(true);
    return () => {
      try { rec.stop(); } catch (_e) { /* noop */ }
    };
  }, []);

  // Stop any in-flight audio + reveal timers when the wizard unmounts.
  useEffect(() => () => {
    if (audioElRef.current) { try { audioElRef.current.pause(); } catch (_e) { /* noop */ } audioElRef.current = null; }
    if (revealTimerRef.current) { clearInterval(revealTimerRef.current); revealTimerRef.current = null; }
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
  }, []);

  // Seed Maya's opening lines into the *pending* queue (not history) so
  // they only appear on screen at the moment Maya starts speaking them.
  // We enqueue every consecutive `ava` step at the top of the script plus
  // the first interactive step's `ava_text` (marked with `stepBubbleForId`
  // so we know when to reveal the answer controls). `stepIdx` skips
  // straight past the AvA-only entries — they live in the queue now.
  useEffect(() => {
    // For non-English visitors, wait until the translated script has
    // resolved (scriptI18n === undefined while in flight) before seeding.
    if (scriptI18n === undefined) return undefined;
    // Also wait for Maya's real voice slot to resolve so the very first
    // line speaks at her true pitch (see greeterLoaded above).
    if (!greeterLoaded) return undefined;
    if (history.length === 0 && pendingAva.length === 0 && !activeAva) {
      const queue = [];
      let i = 0;
      while (i < ACTIVE_SCRIPT.length && ACTIVE_SCRIPT[i].type === 'ava') {
        queue.push({ text: ACTIVE_SCRIPT[i].text });
        i += 1;
      }
      const firstInteractive = ACTIVE_SCRIPT[i];
      if (firstInteractive && firstInteractive.ava_text) {
        queue.push({ text: firstInteractive.ava_text, stepBubbleForId: firstInteractive.id });
      }
      // 2-second breather before Maya starts talking — gives the teen a
      // moment to take the page in (Robert's request). The TTS prefetch
      // is already running in parallel, so the pause costs nothing.
      const t = setTimeout(() => {
        if (queue.length) setPendingAva(queue);
        setStepIdx(i);
      }, 2000);
      return () => clearTimeout(t);
    }
    return undefined;
    // Seeds Maya's opening lines exactly once — after the i18n script has
    // resolved (guard above keeps re-runs no-ops once history exists).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptI18n, greeterLoaded]);

  // Voice-off / accessibility fallback: if the teen mutes Maya (or the
  // browser blocks synthesis entirely), collapse the queue-driven reveal
  // into an immediate flush — every waiting bubble lands in history at
  // full text, controls appear, wizard remains fully usable without audio.
  useEffect(() => {
    if (voiceEnabled) return;
    if (revealTimerRef.current) { clearInterval(revealTimerRef.current); revealTimerRef.current = null; }
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    if (activeAva || pendingAva.length > 0) {
      const flushed = [];
      if (activeAva && !spokenTextsRef.current.has(activeAva.text.trim())) {
        flushed.push({ from: 'ava', text: activeAva.text });
        spokenTextsRef.current.add(activeAva.text.trim());
      }
      pendingAva.forEach((p) => {
        if (spokenTextsRef.current.has(p.text.trim())) return;
        flushed.push({ from: 'ava', text: p.text });
        spokenTextsRef.current.add(p.text.trim());
      });
      if (flushed.length) setHistory((h) => [...h, ...flushed]);
      setActiveAva(null);
      setPendingAva([]);
      lineInFlightRef.current = null;
      if (audioElRef.current) { try { audioElRef.current.pause(); } catch (_e) { /* noop */ } audioElRef.current = null; }
      setIsSpeaking(false);
    }
  }, [voiceEnabled, activeAva, pendingAva]);

  // Resolve Maya's TTS voice — identical slot → base-voice + pitch
  // resolution to the main chat player (useAvAVoicePlayback), so
  // registration Maya sounds exactly like in-app Maya.
  const BASE_OPENAI_VOICES = { nova: 1, shimmer: 1, coral: 1, alloy: 1, echo: 1, ash: 1, fable: 1, onyx: 1, sage: 1 };
  const storedVoice = greeter.voice || 'tf1';
  const voiceSlot = TEEN_VOICES.find((v) => v.id === storedVoice);
  const ttsVoice = voiceSlot?.baseVoice || (BASE_OPENAI_VOICES[storedVoice] ? storedVoice : 'nova');
  const ttsPitch = voiceSlot?.pitch || 1.0;

  // Fetch (and cache) the voice-library mp3 for a script line. Public
  // endpoint, pre-auth — server-side it's whitelisted to Maya's known
  // script lines and disk-cached, so repeat lines return instantly.
  const fetchLineAudio = (voice, text) => {
    const key = `${voice}|${text}`;
    if (!audioCacheRef.current.has(key)) {
      const p = fetch(`${API_URL}/api/tts/registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice }),
      })
        .then((r) => r.json())
        .then((d) => {
          const audio = d && d.success && d.audio ? d.audio : null;
          // Never cache a failure — a transient TTS/network hiccup must
          // retry on the next attempt, not leave the line silent forever.
          if (!audio) audioCacheRef.current.delete(key);
          return audio;
        })
        .catch(() => { audioCacheRef.current.delete(key); return null; });
      audioCacheRef.current.set(key, p);
    }
    return audioCacheRef.current.get(key);
  };

  // Warm the ENTIRE script's audio in the background as soon as the
  // wizard opens (3 workers, sequential within each). By the time the
  // teen reaches Q2 every line — including Q18/Q22 deep in the OCEAN
  // block — is already in the browser cache, so her voice starts
  // instantly instead of racing the watchdog on a cold TTS generation.
  useEffect(() => {
    const lines = [];
    ACTIVE_SCRIPT.forEach((s) => {
      if (s.type === 'ava' && s.text) lines.push(s.text);
      if (s.ava_text) lines.push(s.ava_text);
    });
    // Skip "{name}" template lines — the name isn't known yet. They're
    // prefetched the moment the teen answers Q1 (see acceptText).
    const ready = lines.filter((t) => !t.includes('{name}'));
    let i = 0;
    let stopped = false;
    const worker = async () => {
      while (!stopped && i < ready.length) {
        const t = ready[i];
        i += 1;
        // eslint-disable-next-line no-await-in-loop
        await fetchLineAudio(ttsVoice, t);
      }
    };
    worker(); worker(); worker();
    return () => { stopped = true; };
    // Re-warms if the greeter resolves a different voice slot after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsVoice]);

  // Reveal engine — one line at a time. Fires whenever we're idle with
  // pending work. Maya speaks through the real voice library (OpenAI TTS
  // mp3) and the words reveal in sync with the ACTUAL audio position via
  // `timeupdate` — the same proven pattern as the main chat. If audio
  // can't start (autoplay blocked, endpoint down, slow network) the text
  // reveals at a reading pace instead — the teen is never stuck.
  useEffect(() => {
    if (!voiceEnabled) return undefined;
    if (activeAva) return undefined;
    if (pendingAva.length === 0) return undefined;

    const next = pendingAva[0];
    if (!next || !next.text) return undefined;

    // Content-based dedupe — the same line never speaks twice. Silently
    // drop it (do NOT push to history again): re-adding it was how a
    // double-tapped answer button produced two identical Maya bubbles.
    if (spokenTextsRef.current.has(next.text.trim())) {
      setPendingAva((p) => p.slice(1));
      return undefined;
    }

    // Double-start guard (StrictMode double-mount, greeter arriving
    // mid-fetch, etc) — one reveal pipeline per line, ever.
    if (lineInFlightRef.current === next.text) return undefined;
    lineInFlightRef.current = next.text;

    const text = next.text;
    // Char index at the END of each word — the unit of reveal.
    const wordEnds = [];
    const re = /\S+/g;
    let m;
    while ((m = re.exec(text))) wordEnds.push(m.index + m[0].length);
    const totalChars = text.length;

    let finished = false;
    let started = false;
    let textOnlyStarted = false;

    const clearTimers = () => {
      if (revealTimerRef.current) { clearInterval(revealTimerRef.current); revealTimerRef.current = null; }
      if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    };

    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimers();
      if (audioElRef.current) { try { audioElRef.current.pause(); } catch (_e) { /* noop */ } audioElRef.current = null; }
      lineInFlightRef.current = null;
      spokenTextsRef.current.add(text.trim());
      setHistory((h) => [...h, { from: 'ava', text }]);
      setActiveAva(null);
      setPendingAva((p) => p.slice(1));
      setIsSpeaking(false);
    };

    // Forward-only char reveal — text can never regress or skip backwards.
    const revealTo = (chars) => {
      setActiveAva((prev) => {
        if (!prev) return prev;
        return { ...prev, revealedChars: Math.max(prev.revealedChars, Math.min(chars, totalChars)) };
      });
    };

    // Paced fallback: one word per tick. `pace` ms/word.
    const startFallbackTimer = (pace, onDone) => {
      if (revealTimerRef.current) clearInterval(revealTimerRef.current);
      let wordIdx = 0;
      revealTimerRef.current = setInterval(() => {
        wordIdx += 1;
        const end = wordEnds[Math.min(wordIdx, wordEnds.length - 1)];
        revealTo(end);
        if (wordIdx >= wordEnds.length - 1) {
          clearInterval(revealTimerRef.current);
          revealTimerRef.current = null;
          if (onDone) onDone();
        }
      }, pace);
    };

    // Text-only path — used when audio can't play. Idempotent.
    const textOnlyReveal = () => {
      if (textOnlyStarted || finished) return;
      textOnlyStarted = true;
      setIsSpeaking(false);
      setActiveAva({ text, stepBubbleForId: next.stepBubbleForId, revealedChars: wordEnds[0] || totalChars });
      startFallbackTimer(240, () => { setTimeout(finish, 500); });
    };

    // Watchdog: if audio hasn't started within 8s (should never happen
    // now that the whole script is prefetched at mount — this only
    // covers a totally dead TTS endpoint), reveal as text so the teen
    // is never staring at an empty panel.
    watchdogRef.current = setTimeout(() => {
      watchdogRef.current = null;
      if (!started && !finished) textOnlyReveal();
    }, 8000);

    fetchLineAudio(ttsVoice, text).then((b64) => {
      if (finished || textOnlyStarted) return;
      if (!b64) { textOnlyReveal(); return; }

      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      // Same pitch treatment as the main chat player (audio.playbackRate).
      audio.playbackRate = Math.max(0.5, Math.min(2.0, ttsPitch));
      audioElRef.current = audio;

      const updateWords = () => {
        if (!audio.duration || finished) return;
        const progress = Math.min(1, audio.currentTime / audio.duration);
        const idx = Math.min(wordEnds.length - 1, Math.max(0, Math.ceil(progress * wordEnds.length) - 1));
        revealTo(wordEnds[idx]);
      };
      audio.addEventListener('timeupdate', updateWords);
      audio.onended = () => { revealTo(totalChars); finish(); };
      audio.onerror = () => { if (started) { finish(); } else { textOnlyReveal(); } };

      audio.play().then(() => {
        if (finished || textOnlyStarted) { try { audio.pause(); } catch (_e) { /* noop */ } return; }
        started = true;
        if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
        setIsSpeaking(true);
        // First word appears the moment her voice starts.
        setActiveAva({ text, stepBubbleForId: next.stepBubbleForId, revealedChars: wordEnds[0] || totalChars });
      }).catch(() => {
        // Autoplay blocked — text still flows; the teen's next tap is the
        // unlocking gesture, so the following line gets audio again.
        textOnlyReveal();
      });

      // Prefetch the next line while this one plays so bubbles chain
      // without an audible gap.
      if (pendingAva[1] && pendingAva[1].text) fetchLineAudio(ttsVoice, pendingAva[1].text);
    });

    // NOTE: no timer cleanup here on purpose. This effect re-fires the
    // moment `activeAva` is set (i.e. as soon as the reveal starts) — a
    // cleanup would kill the in-flight interval and freeze the text at
    // the first word. Timers are cleared in finish(), flushAvaToHistory(),
    // the voice-off flush, and the unmount effect above.
    return undefined;
    // Verified deps: fires exactly when we transition into an idle state
    // with pending work, or when voice gets re-enabled / greeter's voice
    // slot resolves. Refs need not be listed. `ACTIVE_SCRIPT` is bound at
    // mount. The lineInFlight guard makes re-fires harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceEnabled, activeAva, pendingAva, ttsVoice, ttsPitch]);

  // Auto-scroll
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [history, stepIdx, activeAva]);

  const step = ACTIVE_SCRIPT[stepIdx];
  // Total interactive questions = inputs + choices + OCEAN (skip ava bubbles).
  // Use this for the "X / N · Y answered" label so the count never includes
  // Maya's narrative bubbles. Mirrors the screenshot Robert shared.
  const questionSteps = ACTIVE_SCRIPT.filter((s) => s.type !== 'ava');
  const totalQuestions = questionSteps.length;
  const currentQuestionIdx = ACTIVE_SCRIPT.slice(0, stepIdx + 1).filter((s) => s.type !== 'ava').length;
  const answeredCount = ACTIVE_SCRIPT.filter((s) => {
    if (s.type === 'ava') return false;
    if (s.type === 'ocean') return !!(formData?.oceanAnswers && formData.oceanAnswers[s.qid]);
    return !!(s.field && formData?.[s.field]);
  }).length;
  const progressPct = Math.round((currentQuestionIdx / Math.max(totalQuestions, 1)) * 100);
  const isComplete = stepIdx >= ACTIVE_SCRIPT.length;

  // Flush any AvA line currently being spoken (or still queued to be
  // spoken) directly into history at full text. Called whenever the teen
  // answers — we don't want Maya "still talking" about the previous
  // question after the teen has moved on. Any in-flight utterance is
  // cancelled and the paced reveal timer is torn down.
  const flushAvaToHistory = () => {
    if (revealTimerRef.current) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
    if (audioElRef.current) { try { audioElRef.current.pause(); } catch (_e) { /* noop */ } audioElRef.current = null; }
    lineInFlightRef.current = null;
    setIsSpeaking(false);
    // Read the FRESH queue via refs (never the render closure — it can be
    // stale inside the memoized controls) and skip anything that already
    // reached history. Both guards together make duplicates impossible.
    const flushed = [];
    const act = activeAvaRef.current;
    if (act && !spokenTextsRef.current.has(act.text.trim())) {
      flushed.push({ from: 'ava', text: act.text });
      spokenTextsRef.current.add(act.text.trim());
    }
    pendingAvaRef.current.forEach((p) => {
      if (spokenTextsRef.current.has(p.text.trim())) return;
      flushed.push({ from: 'ava', text: p.text });
      spokenTextsRef.current.add(p.text.trim());
    });
    if (flushed.length) setHistory((h) => [...h, ...flushed]);
    setActiveAva(null);
    setPendingAva([]);
  };

  // Advance past any consecutive AvA-only steps and enqueue their lines
  // for voice-driven reveal. Stops on the first interactive step and
  // enqueues that step's `ava_text` too (marked with `stepBubbleForId`
  // so the render layer knows when to reveal the answer controls).
  const advanceAva = (fromIdx) => {
    let i = fromIdx;
    const queued = [];
    while (i < ACTIVE_SCRIPT.length && ACTIVE_SCRIPT[i].type === 'ava') {
      queued.push({ text: personalize(ACTIVE_SCRIPT[i].text) });
      i += 1;
    }
    const nextInteractive = ACTIVE_SCRIPT[i];
    if (nextInteractive && nextInteractive.ava_text) {
      queued.push({ text: personalize(nextInteractive.ava_text), stepBubbleForId: nextInteractive.id });
    }
    if (queued.length) setPendingAva((p) => [...p, ...queued]);
    setStepIdx(i);
  };

  const acceptText = (text) => {
    if (!step || !text || !text.trim()) return;
    if (lastAnsweredStepRef.current === step.id) return;
    // Age gate (QA report M-05): HiBFF consumer accounts are strictly 13-19.
    // Maya must never silently accept an out-of-range age.
    if (step.field === 'age') {
      const n = parseInt(String(text).replace(/\D/g, ''), 10);
      if (!n || n < 13 || n > 19) {
        flushAvaToHistory();
        setHistory((h) => [
          ...h,
          { from: 'teen', text: text.trim() },
          { from: 'ava', text: "Hmm — HiBFF is for teens aged 13 to 19, so that doesn't look right. Can you give me your real age? It should match the date of birth you entered. 😊" },
        ]);
        setDraft('');
        return;
      }
    }
    lastAnsweredStepRef.current = step.id;
    if (step.field === 'firstName') {
      // Name just arrived — remember it and warm the voice cache for
      // every personalised line so they play instantly when reached.
      nameRef.current = text.trim();
      ACTIVE_SCRIPT.forEach((s) => {
        const line = s.type === 'ava' ? s.text : s.ava_text;
        if (line && line.includes('{name}')) fetchLineAudio(ttsVoice, personalize(line));
      });
    }
    flushAvaToHistory();
    setHistory((h) => [...h, { from: 'teen', text: text.trim() }]);
    onAnswer(step.field, text.trim());
    setDraft('');
    setChips([]);
    advanceAva(stepIdx + 1);
  };

  // Multi-value inputs (e.g. hobbies): stage each entry as a chip, then
  // submit them all joined so the teen can list several pursuits.
  const addChip = () => {
    const v = draft.trim();
    if (!v) return;
    if (chips.some((c) => c.toLowerCase() === v.toLowerCase())) { setDraft(''); return; }
    setChips((c) => [...c, v]);
    setDraft('');
  };
  const removeChip = (i) => setChips((c) => c.filter((_, idx) => idx !== i));
  const submitMulti = () => {
    const all = [...chips, draft.trim()].map((s) => s.trim()).filter(Boolean);
    if (!all.length) return;
    acceptText(all.join(', '));
  };

  const acceptChoice = (choice, label) => {
    if (!step) return;
    if (lastAnsweredStepRef.current === step.id) return;
    lastAnsweredStepRef.current = step.id;
    flushAvaToHistory();
    setHistory((h) => [...h, { from: 'teen', text: label || choice }]);
    onAnswer(step.field, choice);
    advanceAva(stepIdx + 1);
  };

  // OCEAN Likert response — same chat flow, but the answer is stored
  // inside `formData.oceanAnswers[qid]` so Register.js can POST the full
  // map to /api/ocean/submit once the user account exists.
  const acceptOcean = (option) => {
    if (!step || step.type !== 'ocean') return;
    if (lastAnsweredStepRef.current === step.id) return;
    lastAnsweredStepRef.current = step.id;
    flushAvaToHistory();
    setHistory((h) => [...h, { from: 'teen', text: option.label }]);
    const nextMap = { ...(formData?.oceanAnswers || {}), [step.qid]: option.value };
    onAnswer('oceanAnswers', nextMap);
    advanceAva(stepIdx + 1);
  };

  const Active = useMemo(() => {
    if (isComplete) {
      // Safety net — if for any reason fewer than 22 answers were captured
      // (state was tampered with, page reload mid-flow, etc) refuse to
      // continue. The Continue button only enables when answeredCount
      // matches totalQuestions. This means a user cannot bypass the
      // OCEAN block by hitting a stale "Back to step 1" path.
      const allAnswered = answeredCount >= totalQuestions;
      const missing = Math.max(0, totalQuestions - answeredCount);
      return (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 text-center"
          data-testid="maya-finish-card">
          <Sparkles className="mx-auto mb-3 text-amber-500" size={32} />
          {allAnswered ? (
            <p className="text-gray-700 text-sm mb-4" data-testid="maya-finish-ready">
              All {totalQuestions} answered. Ready to continue and create your account?
            </p>
          ) : (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-left" data-testid="maya-finish-missing">
              <p className="text-sm font-semibold text-amber-900 mb-1">Almost there — {missing} answer{missing === 1 ? '' : 's'} still missing.</p>
              <p className="text-xs text-amber-800">
                Hit &quot;Back&quot; and finish the remaining {missing} question{missing === 1 ? '' : 's'}. Every answer helps me match you with the right Practice Partner — no skipping!
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onBack}
              className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              data-testid="maya-back-btn"
            >
              Back
            </button>
            <button
              type="button"
              onClick={allAnswered ? onComplete : undefined}
              disabled={!allAnswered}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${allAnswered ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              data-testid="maya-continue-btn"
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        </div>
      );
    }
    if (!step) return null;
    if (step.type === 'input') {
      const isMulti = !!step.multi;
      return (
        <div className="space-y-3" data-testid={`maya-input-${step.id}`}>
          {isMulti && (
            <p className="text-xs text-gray-500" data-testid={`maya-multi-hint-${step.id}`}>
              Add as many as you like — type one and press Enter (or tap +). Tap Send when you're done.
            </p>
          )}
          {isMulti && chips.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid={`maya-chips-${step.id}`}>
              {chips.map((c, i) => (
                <span key={`${c}-${i}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-purple-100 text-purple-800 text-sm font-medium"
                  data-testid={`maya-chip-${step.id}-${i}`}>
                  {c}
                  <button type="button" onClick={() => removeChip(i)} aria-label={`Remove ${c}`}
                    className="ml-0.5 p-1 -mr-1 rounded-full text-purple-500 hover:text-purple-800 hover:bg-purple-200" data-testid={`maya-chip-remove-${step.id}-${i}`}>
                    <X size={16} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (isMulti) { addChip(); } else { acceptText(draft); }
              }}
              placeholder={isListening ? 'Listening…' : step.placeholder}
              className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-white text-gray-900 placeholder-gray-400 border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
              data-testid={`maya-input-field-${step.id}`}
            />
            {isMulti && (
              <button
                type="button"
                onClick={addChip}
                disabled={!draft.trim()}
                className="p-3 rounded-xl bg-purple-100 hover:bg-purple-200 text-purple-700 disabled:opacity-40 flex items-center justify-center flex-shrink-0"
                data-testid={`maya-add-chip-${step.id}`}
                aria-label="Add"
              >
                <Plus size={18} />
              </button>
            )}
            {/* Mic button — browser-native speech recognition, no backend */}
            {micSupported && (
              <button
                type="button"
                onClick={() => {
                  if (isListening) {
                    try { recognitionRef.current && recognitionRef.current.stop(); } catch (_e) { /* noop */ }
                    setIsListening(false);
                  } else {
                    // Pause Maya's voice so it doesn't feed into the mic.
                    try { if (audioElRef.current) audioElRef.current.pause(); } catch (_e) { /* noop */ }
                    try {
                      if (!recognitionRef.current) return;
                      setDraft('');
                      recognitionRef.current.start();
                      setIsListening(true);
                    } catch (_e) { setIsListening(false); /* already-started / unsupported — never crash */ }
                  }
                }}
                className={`p-3 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
                  isListening ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                data-testid={`maya-mic-${step.id}`}
                aria-label={isListening ? 'Stop recording' : 'Start voice input'}
              >
                {isListening ? <MicOff size={18} /> : <Mic size={18} />}
              </button>
            )}
            <button
              type="button"
              onClick={() => (isMulti ? submitMulti() : acceptText(draft))}
              disabled={isMulti ? (chips.length === 0 && !draft.trim()) : !draft.trim()}
              className="p-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40 flex items-center justify-center flex-shrink-0"
              data-testid={`maya-input-submit-${step.id}`}
              aria-label="Send"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      );
    }
    if (step.type === 'choice') {
      return (
        <div className="space-y-3" data-testid={`maya-choice-${step.id}`}>
          <div className="grid grid-cols-3 gap-2">
            {step.options.map((opt) => {
              const val = typeof opt === 'object' ? opt.value : opt;
              const label = typeof opt === 'object' ? opt.label : opt;
              return (
                <button
                  type="button"
                  key={val}
                  onClick={() => acceptChoice(val, label)}
                  className="px-3 py-2.5 rounded-xl bg-white text-gray-800 border border-gray-200 hover:border-purple-500 hover:bg-purple-50 text-sm font-medium transition-colors"
                  data-testid={`maya-choice-option-${step.id}-${val}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    if (step.type === 'ocean') {
      // Single chat shell — same Maya bubble, same look as the preference
      // choices. Five Likert pills running left → right (auto-wraps on
      // narrow screens). Same data-testid namespace so testing keeps simple.
      return (
        <div className="space-y-3" data-testid={`maya-ocean-${step.id}`}>
          <div className="flex flex-wrap gap-2 justify-center">
            {step.options.map((opt) => (
              <button
                type="button"
                key={opt.value}
                onClick={() => acceptOcean(opt)}
                className="px-4 py-2.5 rounded-full bg-white text-gray-800 border border-gray-200 hover:border-purple-500 hover:bg-purple-50 text-sm font-medium transition-colors whitespace-nowrap"
                data-testid={`maya-ocean-option-${step.id}-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    return null;
    // The memoized input block is a pure JSX helper that references stable
    // event handlers (`acceptText`, `acceptChoice`, `acceptOcean`) and the
    // `isListening`/`micSupported` state indirectly via nested closures.
    // Adding every closure value would defeat the memo's purpose (it would
    // then rebuild on every keystroke of an unrelated field). Keyed on the
    // primary drivers — the current `step` shape, `draft` text, question
    // progress, and greeter identity — which are the values that actually
    // change what's rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, draft, chips, isListening, micSupported, isComplete, greeter.name, formData?.oceanAnswers, answeredCount, totalQuestions]);

  const initial = (greeter.name || 'M').charAt(0).toUpperCase();

  return (
    <div className="space-y-4" data-testid="maya-profile-chat">
      <div className="text-center mb-1">
        <h2 className="text-lg font-bold text-gray-900">Get to know {greeter.name}</h2>
        <p className="text-sm text-gray-500 mt-1">Your AvA — she&apos;ll guide your first moments here.</p>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
          <span className="font-semibold uppercase tracking-wider">Chatting with {greeter.name}</span>
          <span data-testid="maya-progress-label">
            {Math.min(currentQuestionIdx, totalQuestions)} / {totalQuestions} · {answeredCount} answered
          </span>
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-orange-500 transition-all"
            style={{ width: `${progressPct}%` }}
            data-testid="maya-progress-bar"
          />
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-col md:flex-row bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Left — Maya portrait */}
        <div className="md:w-72 md:flex-shrink-0 p-5 bg-gradient-to-b from-slate-50 to-slate-100 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col items-center">
          <div className="relative w-44 h-44 md:w-48 md:h-48 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white"
            data-testid="maya-portrait">
            {greeter.idle_loop_url ? (
              <IdleLoopVideo
                src={`${API_URL}/api/files/${greeter.idle_loop_url}`}
                poster={greeter.avatar_url || undefined}
                className="w-full h-full object-cover"
                testId="maya-idle-loop-video"
              />
            ) : greeter.avatar_url ? (
              <img
                src={greeter.avatar_url}
                alt={greeter.name}
                fetchpriority="high"
                decoding="async"
                className="w-full h-full object-cover maya-breathe"
                data-testid="maya-static-image"
                onError={(e) => {
                  e.currentTarget.src = `https://api.dicebear.com/7.x/lorelei/svg?seed=${encodeURIComponent(greeter.name || 'Maya')}&backgroundColor=c7d2fe`;
                }}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-300 to-pink-200 flex items-center justify-center text-5xl font-bold text-white">
                {initial}
              </div>
            )}
          </div>

          {!demoMode && (
            <p className="mt-3 text-xs font-semibold text-amber-700 text-center leading-snug" data-testid="maya-mandatory-warning">
              ⚠️ All questions must be answered. Failure to do so will prevent your account from being opened.
            </p>
          )}

          <div className="mt-4 text-center">
            <h3 className="text-xl font-bold text-gray-900" data-testid="maya-name">
              {greeter.name}
            </h3>
            <p className="text-xs text-purple-600 font-semibold uppercase tracking-wider mt-0.5">
              Your practice partner
            </p>
            <div className="mt-2 flex items-center justify-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${isSpeaking ? 'bg-purple-500' : 'bg-green-500'} animate-pulse`} />
              <span className="text-sm text-gray-600 font-medium">
                {isSpeaking ? 'Speaking…' : 'Ready'}
              </span>
            </div>
            {/* Voice toggle — teen can mute Maya's voice if they prefer text only */}
            <button
              type="button"
              onClick={() => {
                const next = !voiceEnabled;
                setVoiceEnabled(next);
                if (!next) {
                  if (audioElRef.current) { try { audioElRef.current.pause(); } catch (_e) { /* noop */ } audioElRef.current = null; }
                  setIsSpeaking(false);
                }
              }}
              className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                voiceEnabled
                  ? 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="maya-voice-toggle"
            >
              {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              {voiceEnabled ? 'Voice on' : 'Voice off'}
            </button>
          </div>
        </div>

        {/* Right — chat log + active step */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-slate-50 to-white min-h-[260px] max-h-[300px]"
            data-testid="maya-chat-history"
          >
            {history.map((h, i) => (
              <Bubble key={i} from={h.from} avaName={h.from === 'ava' ? greeter.name : undefined}>
                {h.text}
              </Bubble>
            ))}
            {/* activeAva — Maya's currently-speaking line. Text is revealed
                char-range by char-range in sync with her actual speech
                (boundary events), with a paced timer as fallback. Moves
                into `history` at full text once she finishes. */}
            {activeAva && (() => {
              const shown = activeAva.text.slice(0, activeAva.revealedChars);
              const stillTyping = activeAva.revealedChars < activeAva.text.length;
              return (
                <Bubble from="ava" avaName={greeter.name}>
                  <span data-testid="maya-active-ava-text">{shown}</span>
                  {stillTyping && (
                    <span
                      className="inline-block ml-1 w-1 h-4 bg-purple-500 align-middle animate-pulse"
                      data-testid="maya-active-ava-cursor"
                      aria-hidden="true"
                    />
                  )}
                </Bubble>
              );
            })()}
          </div>

          {/* Active input controls — only visible once the current step's
              ava_text has *arrived* on screen (either being spoken now or
              already in history). While the line is still pending in the
              queue, the controls stay hidden so the teen isn't tempted
              to answer a question they haven't been asked yet. Voice-off
              path flushes everything to history, so this gate opens
              immediately in that fallback too. */}
          <div className="p-4 bg-white border-t border-gray-200" data-testid="maya-active-step">
            {(() => {
              const stepBubblePending = step && pendingAva.some((p) => p.stepBubbleForId === step.id);
              if (stepBubblePending) return null;
              return Active;
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MayaProfileChat;
